import { registerDecorators as _registerDecorators, LightningElement, registerComponent as _registerComponent } from "lwc";
import _tmpl from "./skillFileViewer.html"; // The RIGHT PANE of the Package Files tab. Renders the breadcrumb, Preview/Code toggle, Copy Code
// button, markdown/code rendering, and loading spinner for the currently selected file. This is a
// presentational child — the parent orchestrator owns file selection, fetching, and caching; this
// child only renders what it's told via @api props.
import { aiAssetEngagement } from '~/modules/analytics/gtmEventTypes/aiAssetAnalyticsContract';
import { WireI18n } from '~/modules/stores/i18nStore';
import { handleException } from '~/modules/utilities/error_utilities';
import { buildRepositoryMarkdownUrls, stripFrontMatter } from '~/modules/utilities/skill_utils';
class SkillFileViewer extends LightningElement {
  constructor(...args) {
    super(...args);
    // The currently selected file's path (full repo-relative path), or '' when no selection.
    this._selectedPath = '';
    // The currently selected file node, or null when no selection.
    this.selectedNode = null;
    // The selected file's body (from parent's cache), or '' when not yet fetched.
    this.selectedFileBody = '';
    // True when the selected file is currently being fetched (show spinner).
    this.isLoading = false;
    this.hasError = false;
    // True when the left sidebar is collapsed; when true this pane shows the expand button in the breadcrumb row.
    this.treeCollapsed = false;
    // The skill's GitHub source coordinates, needed for breadcrumb's skill-root prefix stripping.
    this.source = null;
    // View mode for the right pane: 'preview' (markdown rendered) or 'code' (raw file in code block).
    this.viewMode = 'preview';
    // reactive
    // Briefly true after clicking the Copy Code control (icon swap only).
    this.rawCopied = false;
    // reactive
    this.copyResetTimer = null;
    this.labels = void 0;
  }
  get selectedPath() {
    return this._selectedPath;
  }
  set selectedPath(value) {
    const nextPath = value ?? '';
    if (nextPath !== this._selectedPath) {
      this.resetRawCopyState();
    }
    this._selectedPath = nextPath;
  }
  // The selected file's extension (without dot), or undefined when no selection.
  get selectedFileExt() {
    return this.selectedNode?.ext;
  }

  // The selected file's name (leaf name), or '' when no selection.
  get selectedFileName() {
    return this.selectedNode?.name ?? '';
  }

  // The selected file's path segments for breadcrumb display (skill-root-relative path split on '/').
  // Returns an array of {name, isLast} objects where the last segment is the filename (bold, dark blue)
  // and earlier segments are folders (regular, gray). Example: for 'assets/agents/actions-reference.md'
  // relative to the skill root, returns [{name:'assets',isLast:false}, {name:'agents',isLast:false},
  // {name:'actions-reference.md',isLast:true}]. For a root-level file, returns [{name:'file.ext',isLast:true}].
  get filePathSegments() {
    if (!this.selectedPath || !this.source) {
      return [];
    }
    // Strip the skill root prefix (e.g. 'skills/agentforce-generate/') from the full repo path.
    const prefix = `${this.source.path}/`;
    const relativePath = this.selectedPath.startsWith(prefix) ? this.selectedPath.slice(prefix.length) : this.selectedPath;
    // Split on '/' to get raw segments. For root-level files, this returns a single-element array.
    const rawSegments = relativePath.split('/').filter(seg => seg.length > 0);
    // Map to {name, isLast} objects so the template can style the last segment (filename) differently.
    return rawSegments.map((name, index) => ({
      key: rawSegments.slice(0, index + 1).join('/'),
      name,
      isLast: index === rawSegments.length - 1
    }));
  }

  // True if the selected file is markdown (ext is 'md' or 'markdown').
  get isMarkdown() {
    const ext = this.selectedFileExt?.toLowerCase();
    return ext === 'md' || ext === 'markdown';
  }

  // The rendered markdown body (front matter stripped), for the Preview view.
  get renderedBody() {
    return stripFrontMatter(this.selectedFileBody);
  }

  // The effective view mode: coerces 'preview' to 'code' for non-markdown files so the toggle
  // displays the correct selection (never shows Preview as selected for code-only files).
  get effectiveViewMode() {
    return this.isMarkdown ? this.viewMode : 'code';
  }

  // Initial entry and file selection are programmatic state synchronization, not user view-mode
  // choices. Suppress the toggle's color transition for that render; a subsequent Preview/Code
  // click re-renders with the same path and therefore keeps the normal transition.
  get disableViewToggleTransition() {
    return this.lastRenderedSelectedPath !== this.selectedPath;
  }
  renderedCallback() {
    this.lastRenderedSelectedPath = this.selectedPath;
  }

  // True if the right pane should show wds-markdown.
  get showPreview() {
    return !this.isLoading && !this.hasError && this.effectiveViewMode === 'preview' && this.isMarkdown;
  }

  // True if the right pane should show wds-code-block.
  get showCode() {
    return !this.isLoading && !this.hasError && (this.effectiveViewMode === 'code' || !this.isMarkdown);
  }

  // The Preview/Code toggle options. Markdown files get both Preview + Code; non-markdown files
  // have no preview, so per the Figma spec we render ONLY the Code option (single selected pill)
  // rather than showing a disabled Preview alongside it.
  get viewToggleOptions() {
    const codeOption = {
      label: this.labels?.code ?? '',
      value: 'code'
    };
    if (!this.isMarkdown) {
      return [codeOption];
    }
    return [{
      label: this.labels?.preview ?? '',
      value: 'preview'
    }, codeOption];
  }

  // The Copy Code button icon: 'copy' or 'check' (briefly after copy).
  get rawCopyIcon() {
    return this.rawCopied ? 'check' : 'copy';
  }

  // CSS class for the right pane body: full-bleed when showing code and centered when showing an error.
  get previewBodyClass() {
    if (this.showCode) {
      return 'preview-body preview-body_full-bleed';
    }
    if (this.hasError) {
      return 'preview-body preview-body_error';
    }
    return 'preview-body';
  }
  get rawLabel() {
    return this.labels?.raw ?? '';
  }
  get viewModeAriaLabel() {
    return this.labels?.viewModeAriaLabel ?? '';
  }
  get packageFilesExpandTree() {
    return this.labels?.packageFilesExpandTree ?? '';
  }
  get packageFilesHeading() {
    return this.labels?.packageFilesHeading ?? '';
  }
  get packageFilesLoadError() {
    return this.labels?.packageFilesLoadError ?? '';
  }
  get packageFilesRetry() {
    return this.labels?.packageFilesRetry ?? '';
  }
  get selectedFileDirectory() {
    const lastSlash = this.selectedPath.lastIndexOf('/');
    return lastSlash >= 0 ? this.selectedPath.slice(0, lastSlash + 1) : '';
  }
  get markdownRepositoryUrls() {
    return buildRepositoryMarkdownUrls(this.source, this.selectedFileDirectory);
  }
  get markdownLinkBaseUrl() {
    return this.markdownRepositoryUrls.linkBaseUrl;
  }
  get markdownImageBaseUrl() {
    return this.markdownRepositoryUrls.imageBaseUrl;
  }
  get markdownAllowedImageRootUrls() {
    return this.markdownRepositoryUrls.allowedImageRootUrls;
  }
  get copyStatus() {
    return this.rawCopied ? this.labels?.copied ?? '' : '';
  }

  // Handler for the expand button (only shown when the sidebar is collapsed): asks the parent to
  // re-open the left sidebar.
  handleToggleTree() {
    this.dispatchEvent(new CustomEvent('toggletree'));
  }
  handleTreeToggleAnalytics(event) {
    event.detail.aiAssetAnalytics = aiAssetEngagement({
      clickText: 'expand file view',
      elementTitle: 'Files',
      elementType: 'sidebar toggle'
    });
  }

  // Called by the package-files orchestrator after collapse so focus lands on the newly rendered
  // expand control instead of falling back to the document body.
  focusTreeToggle() {
    this.template?.querySelector('wds-icon-interactive')?.focusButton();
  }
  handleRetry() {
    this.dispatchEvent(new CustomEvent('retry'));
  }

  // Handler for wds-button-toggle 'select' event (Preview/Code toggle). The WDS primitive owns
  // analytics instrumentation; this consumer only owns the controlled value.
  handleViewToggle(event) {
    const value = event.detail?.value;
    if (value) {
      this.viewMode = value;
    }
  }
  handleViewToggleAnalytics(event) {
    const name = event.detail?.button?.callToAction?.name;
    const clickText = name === 'skill_file_viewer_code' ? 'code' : 'preview';
    event.detail.aiAssetAnalytics = aiAssetEngagement({
      clickText,
      elementTitle: 'Files',
      elementType: 'toggle button group'
    });
  }

  // Stamp the file path onto the Copy Code button's auto-emitted analytics event so the copy click
  // records which file was copied.
  handleCopyAnalytics(event) {
    event.detail.filePath = this.selectedPath;
    event.detail.aiAssetAnalytics = aiAssetEngagement({
      clickText: 'copy code',
      elementTitle: 'Files',
      elementType: 'button'
    });
  }
  handleMarkdownLinkClick(event) {
    this.dispatchEvent(new CustomEvent('analytics', {
      bubbles: true,
      composed: true,
      detail: {
        eventType: 'click',
        elementType: 'marketplaceMarkdownLink',
        aiAssetAnalytics: aiAssetEngagement({
          clickText: event.detail.text,
          clickUrl: event.detail.url,
          elementTitle: 'Files',
          elementType: 'link text'
        })
      }
    }));
  }

  // Handler for the Copy Code button: copies the verbatim file body and briefly swaps the icon.
  async handleCopyRaw() {
    try {
      await navigator.clipboard.writeText(this.selectedFileBody);
    } catch (error) {
      handleException(error, 'skillFileViewer.handleCopyRaw', {
        level: 'warn'
      });
      return;
    }
    this.rawCopied = true;
    if (this.copyResetTimer) {
      clearTimeout(this.copyResetTimer);
    }
    this.copyResetTimer = setTimeout(() => {
      this.rawCopied = false;
      this.copyResetTimer = null;
    }, 2000);
  }
  resetRawCopyState() {
    this.rawCopied = false;
    if (this.copyResetTimer) {
      clearTimeout(this.copyResetTimer);
      this.copyResetTimer = null;
    }
  }
  disconnectedCallback() {
    this.resetRawCopyState();
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(SkillFileViewer, {
  publicProps: {
    selectedPath: {
      config: 3
    },
    selectedNode: {
      config: 0
    },
    selectedFileBody: {
      config: 0
    },
    isLoading: {
      config: 0
    },
    hasError: {
      config: 0
    },
    treeCollapsed: {
      config: 0
    },
    source: {
      config: 0
    }
  },
  publicMethods: ["focusTreeToggle"],
  wire: {
    labels: {
      adapter: WireI18n,
      dynamic: [],
      config: function ($cmp) {
        return {
          selector: i18n => i18n.skills
        };
      }
    }
  },
  fields: ["_selectedPath", "viewMode", "rawCopied", "copyResetTimer"]
});
const __lwc_component_class_internal = _registerComponent(SkillFileViewer, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;